import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Copy, Mail, MessagesSquare } from "lucide-react";
import { ChapterHeading } from "../ChapterHeading";
import { Reveal } from "../motion/Reveal";
import { track, EVENTS } from "../../lib/analytics";

const SITUATION = {
  label: "Situation 014 — Deadlines & Execution",
  title: "Employee missed an important deadline.",
  subject: "Missed Deadline on [insert project/task]",
  opener:
    "I wanted to talk about the missed deadline on [insert project/task]. The date had been agreed, and I want to understand what happened and how we handle timelines better from here.",
  tones: {
    warm: {
      label: "Warm",
      paragraphs: [
        "Hey [Name], I wanted to talk about the deadline for [insert project/task] that was missed. I know things come up, and I don't want to assume the worst here.",
        "That date mattered, and when it slipped, we didn't have a recovery plan in place that worked for [insert impact]. If something got in the way, I'd like to understand what happened so we can plan around it better next time.",
        "Going forward, if you can see a deadline slipping, let's talk early enough to put together a plan that actually works. That gives us options we don't have once the date has already passed.",
      ],
    },
    balanced: {
      label: "Balanced",
      paragraphs: [
        "Hey [Name], I need to talk about the missed deadline on [insert project/task]. The date had been agreed, and when it wasn't met, we didn't have a workable recovery plan in place.",
        "That created a knock-on effect on [insert impact], and I want to avoid that happening again.",
        "If a deadline is at risk, I need to hear about it early enough that we can work out a plan together, not once the date has already gone.",
        "Can you walk me through what happened this time, and let's agree on how we handle timelines from here?",
      ],
    },
    firm: {
      label: "Firm",
      paragraphs: [
        "Hey [Name], I need to address the missed deadline on [insert project/task]. The date was agreed in advance, and it wasn't met, without a recovery plan that could actually address [insert impact].",
        "This needs to change going forward. If a deadline is at risk, I expect to know as soon as you're aware of it, along with a realistic plan for managing it, not after the date has already passed.",
        "I need to see a clear improvement in how deadlines are managed and escalated from here.",
      ],
    },
  },
};

const ORDER = ["warm", "balanced", "firm"];

export function Demo() {
  const [tone, setTone] = useState("warm");
  const [copied, setCopied] = useState(false);

  const pickTone = (t) => {
    setTone(t);
    setCopied(false);
    track(EVENTS.DEMO_TONE, { tone: t });
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SITUATION.tones[tone].paragraphs.join("\n\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <section id="demo" className="border-y border-line bg-surface/60 py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <ChapterHeading
          index="02"
          eyebrow="Try it"
          title={
            <>
              Choose how you want to <em className="font-normal">say it.</em>
            </>
          }
        />
        <Reveal delay={0.1} className="mt-14">
          <div className="overflow-hidden rounded-2xl border border-line bg-white shadow-[0_30px_60px_-40px_rgba(20,19,17,0.25)]">
            <div className="flex flex-col gap-4 border-b border-line px-6 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-8">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-faint">
                  {SITUATION.label}
                </p>
                <p className="mt-1.5 text-lg font-medium text-ink">{SITUATION.title}</p>
              </div>
              <div
                role="tablist"
                aria-label="Message tone"
                className="inline-flex w-fit items-center rounded-full border border-line bg-paper p-1"
              >
                {ORDER.map((t) => (
                  <button
                    key={t}
                    role="tab"
                    aria-selected={tone === t}
                    data-testid={`demo-tab-${t}`}
                    onClick={() => pickTone(t)}
                    className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${
                      tone === t ? "bg-ink text-paper" : "text-smoke hover:text-ink"
                    }`}
                  >
                    {SITUATION.tones[t].label}
                  </button>
                ))}
              </div>
            </div>
            <div className="px-6 py-8 sm:px-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={tone}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.28, ease: "easeOut" }}
                >
                  <div
                    data-testid="demo-message-text"
                    className="max-w-2xl space-y-4 text-[15px] leading-relaxed text-ink sm:text-base"
                  >
                    {SITUATION.tones[tone].paragraphs.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>
              <div className="mt-8 grid gap-px overflow-hidden rounded-lg border border-line bg-line sm:grid-cols-2">
                <div className="bg-paper p-5">
                  <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-faint">
                    <Mail className="h-3.5 w-3.5" /> Email subject
                  </p>
                  <p className="mt-2 text-sm text-ink">{SITUATION.subject}</p>
                </div>
                <div className="bg-paper p-5">
                  <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-faint">
                    <MessagesSquare className="h-3.5 w-3.5" /> Face-to-face opener
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-ink">{SITUATION.opener}</p>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                <p className="text-sm text-faint">
                  This is 1 of 100 difficult management situations inside SayIt.
                </p>
                <button
                  type="button"
                  data-testid="demo-copy-message-button"
                  onClick={copy}
                  className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2 text-sm font-medium text-ink transition-colors duration-200 hover:border-ink/30"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 text-emerald-600" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" /> Copy this message
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
